// src/pages/Users/Galleries/components/GalleryCard.jsx

import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCompressAlt,
  FaEye,
  FaImage,
  FaPen,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { deleteGallery } from "../../../../services/gallerieServices";
import { toAbsolutePhotoUrl } from "../../../../services/albumPhotoServices";

import EditAlbumModal from "../EditAlbumModal";

import {
  FALLBACK_ALBUM_IMG,
  countPhotos,
  getCoverUrl,
  getOrderedPhotos,
  stripHtml,
} from "../utils/galleriesUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "gallery-card-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-orange-600",
  "hover:prose-a:underline",
  "dark:prose-a:text-orange-300",

  // Listes à puces
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",

  // Listes numérotées
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",

  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-orange-500",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Médias masqués dans l’aperçu
  "[&_img]:hidden",
  "[&_iframe]:hidden",
  "[&_video]:hidden",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK_ALBUM_IMG;
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function GalleryCard({
  gallery,
  userRole,
  isManager,
  onPreview,
  refreshGalleriesList,
}) {
  const navigate = useNavigate();

  const [editModalOpen, setEditModalOpen] =
    useState(false);

  const [isDescriptionTruncated, setIsDescriptionTruncated] =
    useState(false);

  const descriptionRef = useRef(null);

  const orderedPhotos = useMemo(() => {
    return getOrderedPhotos(gallery);
  }, [gallery]);

  const coverUrl = getCoverUrl(gallery);
  const photoCount = countPhotos(gallery);

  const previewThumbs = orderedPhotos.slice(0, 4);

  /*
   * On conserve le véritable HTML.
   * La limitation sera uniquement visuelle.
   */
  const descriptionHtml = useMemo(() => {
    const rawDescription = gallery?.description || "";
    const plainDescription = stripHtml(rawDescription);

    if (!plainDescription) {
      return sanitizeHtml(
        "<p><em>Pas de description pour cet album.</em></p>",
      );
    }

    return sanitizeHtml(rawDescription);
  }, [gallery?.description]);

  /*
   * Détecte si la description est coupée.
   */
  useLayoutEffect(() => {
    const element = descriptionRef.current;

    if (!element) {
      setIsDescriptionTruncated(false);
      return undefined;
    }

    let animationFrameId;

    const checkOverflow = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        const hasOverflow =
          element.scrollHeight > element.clientHeight + 2;

        setIsDescriptionTruncated(hasOverflow);
      });
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    resizeObserver?.observe(element);
    window.addEventListener("resize", checkOverflow);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [descriptionHtml, gallery?.id]);

  const handleDeleteGallery = async () => {
    const confirmed = window.confirm(
      "Êtes-vous sûr de vouloir supprimer cette galerie ?",
    );

    if (!confirmed) return;

    try {
      await deleteGallery(gallery.id);

      toast.success("Galerie supprimée avec succès.");

      refreshGalleriesList?.();
    } catch (error) {
      console.error(
        "Erreur lors de la suppression de la galerie :",
        error,
      );

      toast.error(
        "Erreur lors de la suppression de la galerie.",
      );
    }
  };

  return (
    <>
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
        {/* Image de couverture */}

        <div className="relative overflow-hidden bg-slate-100 dark:bg-slate-950">
          <button
            type="button"
            onClick={onPreview}
            className="block w-full text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-orange-500/40"
            aria-label={`Prévisualiser ${
              gallery?.titre || "album"
            }`}
          >
            <div className="aspect-[16/12] w-full sm:aspect-[16/11]">
              <img
                src={coverUrl}
                alt={gallery?.titre || "Album"}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
                loading="lazy"
                onError={onImgError}
              />
            </div>

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/15 to-transparent" />

            <div className="absolute left-4 top-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-950 shadow-sm">
                <FaImage className="text-orange-500" />

                {photoCount} photo
                {photoCount > 1 ? "s" : ""}
              </span>
            </div>

            <div className="absolute right-4 top-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition group-hover:scale-105">
                <FaEye />
              </span>
            </div>

            {!gallery?.is_active ? (
              <div className="absolute left-4 top-16">
                <span className="inline-flex rounded-full bg-amber-400 px-3 py-1 text-xs font-bold uppercase text-amber-950 shadow-sm">
                  Masqué
                </span>
              </div>
            ) : null}

            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="line-clamp-2 text-2xl font-bold leading-tight text-white drop-shadow-sm">
                {gallery?.titre || "Album sans titre"}
              </h3>
            </div>
          </button>
        </div>

        {/* Contenu de la carte */}

        <div className="flex flex-1 flex-col p-5">
          {/* Description enrichie limitée */}

          <div className="relative min-h-[110px]">
            <div
              ref={descriptionRef}
              className={cn(
                RICH_TEXT_CLASSES,
                "max-h-[130px] overflow-hidden pr-1",
              )}
              style={{ tabSize: 4 }}
              dangerouslySetInnerHTML={{
                __html: descriptionHtml,
              }}
            />

            {isDescriptionTruncated ? (
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 h-16",
                  "bg-gradient-to-t from-white via-white/95 to-transparent",
                  "dark:from-slate-900 dark:via-slate-900/95",
                )}
                aria-hidden="true"
              />
            ) : null}
          </div>

          {/* Miniatures */}

          {previewThumbs.length > 0 ? (
            <div className="mt-4 flex gap-2">
              {previewThumbs.map((photo, index) => {
                const thumbUrl =
                  toAbsolutePhotoUrl(
                    photo?.photo_url || "",
                  ) || FALLBACK_ALBUM_IMG;

                return (
                  <button
                    key={
                      photo.id ||
                      `${gallery.id}-${index}`
                    }
                    type="button"
                    onClick={onPreview}
                    className="h-14 flex-1 overflow-hidden rounded-2xl border border-white bg-slate-100 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:shadow dark:border-slate-800 dark:bg-slate-950 dark:ring-slate-700"
                    aria-label={`Prévisualiser la photo ${
                      index + 1
                    }`}
                  >
                    <img
                      src={thumbUrl}
                      alt={stripHtml(
                        photo?.description ||
                          `Photo ${index + 1}`,
                      )}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={onImgError}
                    />
                  </button>
                );
              })}
            </div>
          ) : null}

          {/* Actions principales */}

          <div className="mt-auto grid grid-cols-2 gap-3 pt-5">
            <button
              type="button"
              onClick={onPreview}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-orange-600 shadow-sm transition hover:bg-orange-50 active:scale-95 dark:border-slate-700 dark:bg-slate-950 dark:text-orange-300 dark:hover:bg-orange-950/30"
            >
              Prévisualiser
              <FaCompressAlt className="text-xs" />
            </button>

            <button
              type="button"
              onClick={() =>
                navigate(
                  `/galerie-photos/album/${gallery.id}`,
                )
              }
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              Voir l’album
              <FaArrowRight className="text-xs" />
            </button>
          </div>

          {/* Actions de gestion */}

          {isManager ? (
            <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-200 pt-3 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setEditModalOpen(true)}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaPen />
                Modifier
              </button>

              <button
                type="button"
                onClick={handleDeleteGallery}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-xs font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          ) : null}
        </div>
      </article>

      {editModalOpen ? (
        <EditAlbumModal
          gallery={gallery}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          refreshList={refreshGalleriesList}
        />
      ) : null}
    </>
  );
}